import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, CardBody, Button, Badge, Label } from "reactstrap";
import { FiTrendingUp, FiBriefcase, FiShoppingCart, FiClock, FiRefreshCcw, FiPieChart, FiArrowUpRight } from "react-icons/fi";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, BarChart, Bar, AreaChart, Area, PieChart, Pie, Legend } from "recharts";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI Date Picker
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const Dashboard = () => {
    const dispatch = useDispatch();
    const [data, setData] = useState(null);

    const [filters, setFilters] = useState({
        filter: { value: "today", label: "Today" },
        from_date: dayjs(),
        to_date: dayjs()
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "custom", label: "Custom Range" }
    ];

    const COLORS = ['#f16436', '#0d6efd', '#10b981', '#ffc107', '#6f42c1'];

    const loadDashboard = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                filter: currentFilters.filter.value,
                from_date: currentFilters.from_date.format('YYYY-MM-DD'),
                to_date: currentFilters.to_date.format('YYYY-MM-DD')
            }).toString();

            const res = await Handler({ method: "GET", url: `/report/dashboard.php?${queryParams}` });
            if (res?.success) {
                setData(res);
            }
        } catch (err) {
            toast.error("Analytics Sync Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter.value !== 'custom') {
            loadDashboard();
        }
    }, [filters.filter.value]);

    // --- Chart Data Mapping ---
    const salesTrend = useMemo(() => {
        const labels = data?.charts?.sales_trend?.labels || [];
        const values = data?.charts?.sales_trend?.datasets?.[0]?.data || [];
        return labels.map((l, i) => ({ name: dayjs(l).format('DD MMM'), sales: values[i] }));
    }, [data]);

    const paymentDistribution = useMemo(() => {
        const labels = data?.charts?.payment_modes?.labels || [];
        const values = data?.charts?.payment_modes?.datasets?.[0]?.data || [];
        return labels.map((l, i) => ({ name: l.toUpperCase(), value: values[i] }));
    }, [data]);

    const topProducts = useMemo(() => {
        const labels = data?.charts?.top_products?.labels || [];
        const values = data?.charts?.top_products?.datasets?.[0]?.data || [];
        return labels.map((l, i) => ({ name: l, amount: values[i] }));
    }, [data]);

    const summary = data?.summary || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-4 bg-light min-vh-100">
                
                {/* Header Section */}
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold m-0 text-dark">Business Dashboard</h2>
                        <p className="text-muted small m-0">Live performance & receivable tracking.</p>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter}
                                onChange={(opt) => {
                                    setFilters({...filters, filter: opt});
                                    if(opt.value !== 'custom') loadDashboard({...filters, filter: opt});
                                }}
                                styles={{ control: (b) => ({ ...b, borderRadius: '10px', minHeight: '45px' }) }}
                            />
                        </div>
                        <Button color="primary" className="rounded-3 border-0 shadow-sm" onClick={() => loadDashboard()}>
                            <FiRefreshCcw />
                        </Button>
                    </div>
                </div>

                {/* Custom Date Range */}
                {filters.filter.value === 'custom' && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4 bg-white">
                        <CardBody className="p-3">
                            <Row className="g-3 align-items-end">
                                <Col md={4}><DatePicker label="From" value={filters.from_date} onChange={(v) => setFilters({...filters, from_date: v})} sx={{width:'100%'}} /></Col>
                                <Col md={4}><DatePicker label="To" value={filters.to_date} onChange={(v) => setFilters({...filters, to_date: v})} sx={{width:'100%'}} /></Col>
                                <Col md={4}><Button color="dark" block className="rounded-3 py-3 fw-bold" onClick={() => loadDashboard()}>Generate Report</Button></Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* KPI Cards */}
                <Row className="g-3 mb-4">
                    <Col lg={3} md={6}>
                        <StatCard title="Net Sales" value={summary?.sales?.net} sub={`Bills: ${summary?.sales?.total_invoices}`} icon={<FiShoppingCart />} color="#0d6efd" bg="#e7f1ff" />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard title="Purchases" value={summary?.purchase?.total} sub={`Paid: ₹${summary?.purchase?.paid}`} icon={<FiBriefcase />} color="#6f42c1" bg="#f3ebff" />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard title="Receipts" value={summary?.payments?.total} sub={`Outstanding: ₹${summary?.outstanding?.toLocaleString()}`} icon={<FiTrendingUp />} color="#198754" bg="#e6f4ea" />
                    </Col>
                    <Col lg={3} md={6}>
                        <StatCard title="Total Outstanding" value={(summary?.outstanding - summary?.payments?.total)} sub="Total Receivables" icon={<FiClock />} color="#dc3545" bg="#ffeef0" highlight />
                    </Col>
                </Row>

                <Row className="g-4">
                    {/* Sales Trend */}
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <div className="p-4 bg-white border-bottom d-flex justify-content-between">
                                <h6 className="fw-bold m-0 text-muted small uppercase ls-1">Revenue Trend</h6>
                                <Badge color="soft-success" className="text-success">Live Tracking</Badge>
                            </div>
                            <CardBody>
                                <div style={{ width: '100%', height: 350 }}>
                                    <ResponsiveContainer>
                                        <AreaChart data={salesTrend}>
                                            <defs>
                                                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#f16436" stopOpacity={0.2} />
                                                    <stop offset="95%" stopColor="#f16436" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={11} />
                                            <YAxis axisLine={false} tickLine={false} fontSize={11} tickFormatter={(v) => `₹${v/1000}k`} />
                                            <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                            <Area type="monotone" dataKey="sales" stroke="#f16436" strokeWidth={3} fill="url(#salesGrad)" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Payment Mode Pie */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm rounded-4 h-100">
                            <div className="p-4 bg-white border-bottom"><h6 className="fw-bold m-0 text-muted small">Collection Mix</h6></div>
                            <CardBody className="d-flex flex-column align-items-center">
                                <div style={{ width: '100%', height: 280 }}>
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie data={paymentDistribution} innerRadius={60} outerRadius={90} paddingAngle={5} dataKey="value">
                                                {paymentDistribution.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip />
                                            <Legend verticalAlign="bottom" height={36}/>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="mt-auto w-100 p-3 bg-light rounded-4 text-center">
                                    <span className="small text-muted d-block">Cash Handling</span>
                                    <h4 className="fw-bold text-dark m-0">₹{summary?.payments?.cash?.toLocaleString()}</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Top Products */}
                    <Col lg={6}>
                        <Card className="border-0 shadow-sm rounded-4">
                            <div className="p-4 border-bottom"><h6 className="fw-bold m-0 text-muted small">Top Products by Value</h6></div>
                            <CardBody>
                                <div style={{ width: '100%', height: 300 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={topProducts} layout="vertical">
                                            <XAxis type="number" hide />
                                            <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} fontSize={12} width={100} />
                                            <Tooltip cursor={{ fill: '#f8fafc' }} />
                                            <Bar dataKey="amount" radius={[0, 10, 10, 0]} barSize={20}>
                                                {topProducts.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Outstanding Summary */}
                    <Col lg={6}>
                        <Card className="border-0 shadow-sm rounded-4 bg-dark text-white h-100 overflow-hidden">
                            <CardBody className="p-5 d-flex flex-column justify-content-center text-center">
                                <div className="bg-danger d-inline-flex p-3 rounded-circle mb-3 align-self-center">
                                    <FiClock size={30} />
                                </div>
                                <h2 className="fw-bold mb-2">₹{parseFloat((summary?.outstanding - summary?.payments?.total) || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}</h2>
                                <h5 className="text-white-50 fw-light">Total Market Outstanding</h5>
                                <div className="mt-4 p-3 bg-white bg-opacity-10 rounded-4 border border-white border-opacity-10">
                                    <p className="m-0 small text-white-50">Net Sales (After Credit Notes/TDS)</p>
                                    <h4 className="fw-bold text-warning m-0">₹{parseFloat((summary?.outstanding - summary?.payments?.total) || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </LocalizationProvider>
    );
};

// Reusable Stat Card
const StatCard = ({ title, value, sub, icon, color, bg, highlight }) => (
    <Card className={`border-0 shadow-sm rounded-4 h-100 bg-white ${highlight ? 'border-start border-4 border-danger' : ''}`}>
        <CardBody className="p-4">
            <div className="d-flex justify-content-between mb-3">
                <div style={{ width: '45px', height: '45px', borderRadius: '12px', backgroundColor: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                    {icon}
                </div>
                <FiArrowUpRight className="text-muted" />
            </div>
            <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
            <h3 className="fw-bold mt-1 mb-1" style={{ color: '#333' }}>
                ₹{parseFloat(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}
            </h3>
            <span className="text-muted extra-small fw-medium opacity-75">{sub}</span>
        </CardBody>
    </Card>
);

export default Dashboard;